import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../supabase/supabase';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../context/I18nContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiShield, FiTruck, FiMinus, FiPlus, FiShoppingBag, FiMapPin, FiCheck } = FiIcons;

export const ProductDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    const { data, error } = await supabase
      .from('products_1739200000000')
      .select('*, supplier:supplier_id(*)')
      .eq('id', id)
      .single();

    if (!error) setProduct(data);
    setLoading(false);
  };

  if (loading) return <div className="min-h-screen pt-32 text-center text-zinc-500 font-bold">{t('common.loading')}</div>;
  if (!product) return <div className="min-h-screen pt-32 text-center text-zinc-500 font-bold">Product niet gevonden.</div>;

  return (
    <div className="min-h-screen pt-24 pb-20 px-6 bg-[#FCFAF8]">
      <div className="container mx-auto max-w-7xl">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 font-bold text-sm mb-12 transition-colors">
          <SafeIcon icon={FiArrowLeft} /> {t('product.back')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-white shadow-2xl shadow-zinc-200/50"
            >
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            </motion.div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="p-6 bg-white rounded-3xl border border-zinc-100 text-center">
                <SafeIcon icon={FiShield} className="w-6 h-6 mx-auto mb-3 text-[#D4B483]" />
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{t('product.quality')}</p>
                <p className="text-xs font-bold text-zinc-900 mt-1">{t('product.vetted')}</p>
              </div>
              <div className="p-6 bg-white rounded-3xl border border-zinc-100 text-center">
                <SafeIcon icon={FiTruck} className="w-6 h-6 mx-auto mb-3 text-[#D4B483]" />
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{t('product.delivery')}</p>
                <p className="text-xs font-bold text-zinc-900 mt-1">{t('product.express')}</p>
              </div>
              <div className="p-6 bg-white rounded-3xl border border-zinc-100 text-center">
                <SafeIcon icon={FiCheck} className="w-6 h-6 mx-auto mb-3 text-[#D4B483]" />
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{t('product.stock')}</p>
                <p className="text-xs font-bold text-zinc-900 mt-1">{product.stock_quantity} {product.price_unit}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col h-full">
            <div className="mb-8">
              <span className="px-4 py-1.5 bg-zinc-900 text-white rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6 inline-block">
                {product.category}
              </span>
              <h1 className="text-6xl font-black text-zinc-900 mb-4 tracking-tighter leading-none">
                {product.name}
              </h1>
              <div className="flex items-center gap-4 text-zinc-500">
                <div className="flex items-center gap-1 text-sm font-bold">
                  <SafeIcon icon={FiMapPin} className="text-[#D4B483]" />
                  {product.origin}
                </div>
                <div className="w-1 h-1 bg-zinc-300 rounded-full" />
                <div className="text-sm font-bold uppercase tracking-widest">
                  {t('product.by')} {product.supplier?.name}
                </div>
              </div>
            </div>

            <div className="p-10 bg-white rounded-[3rem] border border-zinc-100 shadow-xl shadow-zinc-200/30 mb-10">
              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-4xl font-black text-zinc-900">€{product.price_amount.toFixed(2)}</span>
                <span className="text-xl text-zinc-400 font-medium">/ {product.price_unit}</span>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 mb-3 block">
                    {t('product.orderQuantity')} ({product.price_unit})
                  </label>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center bg-zinc-50 rounded-2xl p-2 border border-zinc-100">
                      <button 
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-xl transition-all"
                      >
                        <SafeIcon icon={FiMinus} />
                      </button>
                      <span className="w-16 text-center font-black text-lg">{quantity}</span>
                      <button 
                        onClick={() => setQuantity(Math.min(product.stock_quantity || 999, quantity + 1))}
                        className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-xl transition-all"
                      >
                        <SafeIcon icon={FiPlus} />
                      </button>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-zinc-400 mb-1">{t('product.totalAmount')}</p>
                      <p className="text-2xl font-black text-zinc-900">€{(product.price_amount * quantity).toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <Button fullWidth size="lg" className="h-16 rounded-2xl text-lg gap-3">
                  <SafeIcon icon={FiShoppingBag} /> {t('product.addToOrder')}
                </Button>
                
                <p className="text-center text-xs text-zinc-400 font-medium tracking-wide">
                  {t('product.minOrderNote')}
                </p>
              </div>
            </div>

            <div className="flex-1">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 mb-6">{t('product.producerBackground')}</h3>
              <div className="flex items-start gap-6 p-8 bg-zinc-900 rounded-[2.5rem] text-white overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4B483]/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                <img 
                  src={product.supplier?.image_url || 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=400'} 
                  className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/10" 
                  alt={product.supplier?.name}
                />
                <div className="relative z-10">
                  <h4 className="text-lg font-black mb-1">{product.supplier?.name}</h4>
                  <p className="text-sm text-white/60 mb-4 line-clamp-2 font-light">
                    {product.supplier?.description || 'Een toegewijde premium producent binnen het Foodrolla-netwerk.'}
                  </p>
                  <Link to={`/supplier/${product.supplier?.id}`} className="text-[#D4B483] text-xs font-black uppercase tracking-widest hover:text-white transition-colors">
                    {t('product.viewProfile')} →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};